import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import apiService from '../utils/api';

export interface ServicesPageProps {
  website: {
    salonInfo: {
      name: string;
      _id?: string;
    };
    content: {
      serviceDescription?: string;
      servicesDescription?: string;
    };
  };
}

interface Service {
  _id: string;
  name: string;
  description?: string;
  price: number;
  duration: number;
  category?: string;
  isActive?: boolean;
}

interface Category {
  _id: string;
  name: string;
  services: Service[];
}

const ServicesPage: React.FC<ServicesPageProps> = ({ website }) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { slug } = useParams<{ slug: string }>();
  
  // servicesDescription veya serviceDescription'dan hangisi varsa onu kullan
  const getServiceDescription = () => {
    if (website.content.servicesDescription) {
      return website.content.servicesDescription;
    } else if (website.content.serviceDescription) {
      return website.content.serviceDescription;
    }
    return "Salonumuzun sunduğu hizmetleri keşfedin."; // Varsayılan değer
  };
  
  useEffect(() => {
    const fetchServices = async () => {
      if (!website.salonInfo._id) {
        setError('Salon kimliği bulunamadı');
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const response = await apiService.getSalonServices(website.salonInfo._id);
        
        if (response.success && response.services) {
          // Kategori bazlı hizmetleri grupla
          const servicesGroupedByCategory: {[key: string]: Service[]} = {};
          const services: Service[] = response.services;
          
          // Hizmetleri kategorilerine göre grupla
          services.forEach((service: Service) => {
            const categoryName = service.category || 'Genel';
            
            if (!servicesGroupedByCategory[categoryName]) {
              servicesGroupedByCategory[categoryName] = [];
            }
            
            servicesGroupedByCategory[categoryName].push(service);
          });
          
          // Kategori objeleri dizisine çevir
          const categoriesArray: Category[] = Object.keys(servicesGroupedByCategory).map(categoryName => ({
            _id: categoryName,
            name: categoryName,
            services: servicesGroupedByCategory[categoryName]
          }));
          
          // Kategorileri alfabetik sırala
          categoriesArray.sort((a, b) => a.name.localeCompare(b.name));
          
          setCategories(categoriesArray);
          
          // İlk kategoriyi aktif yap (eğer varsa)
          if (categoriesArray.length > 0) {
            setActiveCategory(categoriesArray[0]._id);
          }
        } else {
          setError('Hizmetler alınamadı');
        }
      } catch (err) {
        console.error('Hizmetler yüklenirken hata:', err);
        setError('Hizmetler yüklenirken bir hata oluştu');
      } finally {
        setLoading(false);
      }
    };
    
    fetchServices();
  }, [website.salonInfo._id]);
  
  // Aktif kategoriyi değiştir
  const handleCategoryClick = (categoryId: string) => {
    setActiveCategory(categoryId);
  };
  
  // Aktif kategorideki hizmetleri bul
  const getActiveServices = () => {
    if (!activeCategory) return [];
    
    const category = categories.find(cat => cat._id === activeCategory);
    return category ? category.services : [];
  };
  
  const activeServices = getActiveServices();
  
  if (loading) {
    return (
      <ServicesPageContainer>
        <PageHeader>
          <div className="container">
            <PageTitle>Hizmetlerimiz</PageTitle>
            <PageDescription>{getServiceDescription()}</PageDescription>
          </div>
        </PageHeader>
        <section className="container">
          <LoadingMessage>Hizmetler yükleniyor...</LoadingMessage>
        </section>
      </ServicesPageContainer>
    );
  }
  
  if (error || categories.length === 0) {
    return (
      <ServicesPageContainer>
        <PageHeader>
          <div className="container">
            <PageTitle>Hizmetlerimiz</PageTitle>
            <PageDescription>{getServiceDescription()}</PageDescription>
          </div>
        </PageHeader>
        <section className="container">
          <ErrorMessage>
            {error || 'Henüz hizmet bulunmamaktadır.'}
          </ErrorMessage>
        </section>
      </ServicesPageContainer>
    );
  }
  
  return (
    <ServicesPageContainer>
      <PageHeader>
        <div className="container">
          <PageTitle>Hizmetlerimiz</PageTitle>
          <PageDescription>{getServiceDescription()}</PageDescription>
        </div>
      </PageHeader>
      
      <section className="container">
        {categories.length > 1 && (
          <ServicesCategories>
            {categories.map((category) => (
              <CategoryButton 
                key={category._id} 
                active={activeCategory === category._id}
                onClick={() => handleCategoryClick(category._id)}
              >
                {category.name}
              </CategoryButton>
            ))}
          </ServicesCategories>
        )}
        
        <TableSection>
          <ServicesTableContainer>
            <ServiceRow header>
              <HeaderCell>Hizmet</HeaderCell>
              <HeaderCell>Süre</HeaderCell>
              <HeaderCell>Fiyat</HeaderCell>
              <HeaderCell></HeaderCell>
            </ServiceRow>
            {activeServices.map((service, index) => (
              <ServiceRow key={service._id} even={index % 2 === 1}>
                <ServiceInfo>
                  <ServiceName>{service.name}</ServiceName>
                  {service.description && (
                    <ServiceDescription>{service.description}</ServiceDescription>
                  )}
                </ServiceInfo>
                <ServiceDuration>
                  <span className="material-icons">schedule</span>
                  {service.duration} dk
                </ServiceDuration>
                <ServicePrice>{service.price} ₺</ServicePrice>
                <ButtonCell>
                  <AppointmentButton to={`/${slug}/randevu?service=${service._id}`}>
                    Randevu Al
                  </AppointmentButton>
                </ButtonCell>
              </ServiceRow>
            ))}
          </ServicesTableContainer>
        </TableSection>
      </section>
    </ServicesPageContainer>
  );
};

const ServicesPageContainer = styled.div`
  margin-top: 80px;
`;

const PageHeader = styled.div`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary}30, ${({ theme }) => theme.colors.secondary}30);
  padding: 80px 0 50px;
  text-align: center;
`;

const PageTitle = styled.h1`
  margin-bottom: 20px;
  font-size: 2.5rem;
  font-weight: 700;
  position: relative;
  display: inline-block;
  background: linear-gradient(45deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  
  &:after {
    content: '';
    display: block;
    width: 80px;
    height: 3px;
    background: linear-gradient(to right, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
    margin: 15px auto 0;
    border-radius: 3px;
  }
`;

const PageDescription = styled.p`
  color: #666;
  font-size: 1.1rem;
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
`;

const ServicesCategories = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin: 40px 0;
  justify-content: center;
`;

const CategoryButton = styled.button<{ active: boolean }>`
  padding: 12px 25px;
  border: none;
  border-radius: 30px;
  background: ${({ active, theme }) => 
    active 
      ? `linear-gradient(45deg, ${theme.colors.primary}, ${theme.colors.secondary})` 
      : '#f5f5f5'
  };
  color: ${({ active }) => active ? 'white' : '#666'};
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
`;

const TableSection = styled.div`
  margin-top: 50px;
`;

const ServicesTableContainer = styled.div`
  width: 100%;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
  margin-bottom: 50px;
`;

interface RowProps {
  even?: boolean;
  header?: boolean;
}

const ServiceRow = styled.div<RowProps>`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  align-items: center;
  padding: 20px 30px;
  background: ${({ even, header, theme }) => 
    header 
      ? `linear-gradient(to right, ${theme.colors.primary}, ${theme.colors.secondary})` 
      : even ? '#fafafa' : 'white'
  };
  color: ${({ header }) => header ? 'white' : 'inherit'};
  
  &:not(:last-child) {
    border-bottom: 1px solid #eee;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 15px 20px;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
    gap: 10px;
    text-align: center;
  }
`;

const HeaderCell = styled.div`
  font-weight: 600;
  color: white;
  
  &:last-child {
    text-align: right;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    display: none;
  }
`;

const ServiceInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const ServiceName = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 5px;
`;

const ServiceDescription = styled.p`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.lightText};
  margin: 0;
`;

const ServiceDuration = styled.div`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.lightText};
  
  span {
    font-size: 18px;
    margin-right: 5px;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    justify-content: center;
  }
`;

const ServicePrice = styled.span`
  font-weight: 700;
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.primary};
`;

const ButtonCell = styled.div`
  text-align: right;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    text-align: center;
  }
`;

const AppointmentButton = styled(Link)`
  display: inline-block;
  text-align: center;
  padding: 10px 25px;
  background: linear-gradient(45deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
  color: white;
  border-radius: 30px;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 50px 0;
  color: ${({ theme }) => theme.colors.lightText};
  font-style: italic;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 50px 0;
  color: ${({ theme }) => theme.colors.error};
  font-style: italic;
`;

export default ServicesPage; 